import { useAuthStore } from '@/stores/authStore';
import type { PermissionFeature, PermissionLevel } from '@/types';

const LEVEL_RANKS: Record<string, number> = {
  none: 0,
  read: 1,
  write: 2,
  manage: 3,
};

export const usePermissions = () => {
  const user = useAuthStore((s) => s.user);

  const isOwner = user?.is_owner ?? false;
  const permissions = user?.permissions ?? {};

  const hasPermission = (feature: PermissionFeature, requiredLevel: PermissionLevel): boolean => {
    if (isOwner) return true;
    const userLevel = permissions[feature] ?? 'none';
    return (LEVEL_RANKS[userLevel] ?? 0) >= (LEVEL_RANKS[requiredLevel] ?? 0);
  };

  return {
    hasPermission,
    isOwner,
    canViewReturns: hasPermission('returns', 'read'),
    canManageReturns: hasPermission('returns', 'write'),
    canViewTeam: hasPermission('team', 'read'),
    canManageTeam: hasPermission('team', 'manage'),
    canViewBilling: hasPermission('billing', 'read'),
    canManageBilling: hasPermission('billing', 'write'),
    canViewIntegrations: hasPermission('integrations', 'read'),
    canManageIntegrations: hasPermission('integrations', 'write'),
    canViewPortalSettings: hasPermission('portal_settings', 'read'),
    canManagePortalSettings: hasPermission('portal_settings', 'write'),
    canViewApiTokens: hasPermission('api_tokens', 'read'),
    canManageApiTokens: hasPermission('api_tokens', 'write'),
    canCreateReturnLabels: hasPermission('return_labels', 'write'),
    canReceiveWarehouseReturns: hasPermission('warehouse_receipts', 'write'),
  };
};
